import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import {
  brand,
  breakpoints,
  tones,
  touchFeedback,
  touchTarget,
  textSize,
  surface,
} from '../../../../styles/brand';

const accent = '#ff9800';
const ink = '#323232';
const muted = '#6b6b6b';
const line = '#e8e8e8';

export const Wrapper = styled.div`
  ${touchFeedback}

  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  font-family: 'RedHatDisplay-Regular';
  color: ${ink};
`;

export const Room = styled.section`
  border: 1px solid ${line};
  border-radius: 1rem;
  background: #fff;
  overflow: hidden;
`;

export const RoomHeader = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.25rem 1rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid ${line};

  h3 {
    margin: 0;
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(1.05)};
  }

  span {
    color: ${muted};
    font-size: ${textSize(0.85)};
    font-variant-numeric: tabular-nums;
  }
`;

export const Rows = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const Row = styled.li<{
  $mark: 'now' | 'next' | null;
  $selected?: boolean;
}>`
  display: grid;
  grid-template-columns: 6.5rem minmax(0, 1fr) auto;
  gap: 0.25rem 1rem;
  align-items: center;
  padding: 0.75rem 1rem;
  min-height: ${touchTarget};

  & + & {
    border-top: 1px solid ${line};
  }

  ${({ $mark }) =>
    $mark === 'now' &&
    css`
      background: #fff7ec;
      box-shadow: inset 3px 0 0 ${accent};
    `}

  /* Equipe aberta no formulário (desktop). */
  ${({ $selected }) =>
    $selected &&
    css`
      background: ${surface.page};
      box-shadow: inset 3px 0 0 ${brand.blue};
    `}

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: minmax(0, 1fr) auto;
  }
`;

export const Time = styled.time`
  font-family: 'RedHatDisplay-Bold';
  font-variant-numeric: tabular-nums;
  font-size: ${textSize(0.95)};

  @media (max-width: ${breakpoints.md}) {
    grid-column: 1 / -1;
    font-size: ${textSize(0.85)};
    color: ${muted};
  }
`;

export const Team = styled.div`
  min-width: 0;

  strong {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.2rem 0.5rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(0.95)};
  }

  small {
    display: block;
    color: ${muted};
    font-size: ${textSize(0.8)};
  }
`;

export const Mark = styled.span<{ $mark: 'now' | 'next' }>`
  font-size: ${textSize(0.68)};
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  ${({ $mark }) =>
    $mark === 'now'
      ? css`
          color: #fff;
          background: ${accent};
        `
      : css`
          color: ${ink};
          background: #f0f0f0;
        `}
`;

export const Side = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
`;

/**
 * Laranja só na equipe da vez ("Agora"/"A seguir"): é a próxima a avaliar.
 * As demais ficam em azul suave, para a coluna de botões não virar ruído.
 */
export const Button = styled.button<{ $primary?: boolean; $quiet?: boolean }>`
  border: 1px solid
    ${({ $primary, $quiet }) =>
      $primary ? brand.orange : $quiet ? 'transparent' : line};
  background: ${({ $primary, $quiet }) =>
    $primary ? brand.orange : $quiet ? tones.blue.soft : brand.white};
  color: ${({ $primary, $quiet }) =>
    $primary ? brand.white : $quiet ? tones.blue.text : ink};
  border-radius: 0.6rem;
  min-height: ${touchTarget};
  padding: 0.4rem 0.85rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.82)};
  cursor: pointer;
  touch-action: manipulation;
  transition: transform 100ms ease-out;

  &:active {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const Editor = styled.form`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem;
  padding: 0.85rem;
  margin-top: 0.4rem;
  border-radius: 0.8rem;
  background: ${surface.page};

  label {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    font-size: ${textSize(0.75)};
    color: ${muted};
  }

  input,
  select {
    min-height: ${touchTarget};
    border: 1px solid ${line};
    border-radius: 0.55rem;
    background: #fff;
    padding: 0 0.7rem;
    /* 16px: o iOS não dá zoom na página ao focar o campo. */
    font-size: ${textSize(1)};
    font-family: 'RedHatDisplay-Bold';
    color: ${ink};
  }

  input {
    width: 6rem;
    font-variant-numeric: tabular-nums;
  }

  select {
    max-width: 16rem;
  }
`;

export const EditorActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
`;

export const ErrorText = styled.p`
  grid-column: 1 / -1;
  flex-basis: 100%;
  margin: 0;
  color: ${tones.red.text};
  font-size: ${textSize(0.82)};
`;

export const Hint = styled.p`
  margin: 0;
  color: ${muted};
  font-size: ${textSize(0.85)};
`;

export const Empty = styled.div`
  padding: 2.5rem 1rem;
  text-align: center;
  color: ${muted};
  border: 1px dashed ${line};
  border-radius: 1rem;
`;

/* ---- Avaliação na aba (FE-65/FE-66) ---- */

export const Summary = styled.div`
  font-variant-numeric: tabular-nums;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem 1rem;

  strong {
    font-family: 'RedHatDisplay-Bold';
    font-variant-numeric: tabular-nums;
  }
`;

/** Desktop: agenda à esquerda, formulário à direita (decidido em 03/11/2026). */
export const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(22rem, 30rem);
  gap: 1.5rem;
  align-items: start;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const AgendaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

/** O formulário acompanha a rolagem da agenda. */
export const FormColumn = styled.div`
  position: sticky;
  top: 1rem;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: 1rem;
`;

export const Points = styled.span`
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.8)};
  font-variant-numeric: tabular-nums;
  color: ${tones.green.text};
  background: ${tones.green.soft};
  border-radius: 999px;
  padding: 0 0.65rem;
  min-height: 1.9rem;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
`;

export const SheetPanel = styled(motion.div)`
  position: fixed;
  inset: 0;
  /* Acima da barra compacta da tela do torneio (4000) e do menu do app
     (1500): eles cobriam o topo da folha (FE-73). */
  z-index: 4500;
  display: flex;
  flex-direction: column;
  background: ${surface.page};
  outline: none;
  font-family: 'RedHatDisplay-Regular';
  color: ${ink};
`;

export const SheetHeader = styled.header`
  ${touchFeedback}

  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: calc(0.5rem + env(safe-area-inset-top)) 0.75rem 0.5rem;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(16px) saturate(180%);
  border-bottom: 1px solid ${line};

  @media (prefers-reduced-transparency: reduce) {
    background: #fff;
    backdrop-filter: none;
  }

  button {
    flex-shrink: 0;
    width: ${touchTarget};
    height: ${touchTarget};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: ${brand.blue};
    cursor: pointer;
  }

  div {
    min-width: 0;
  }

  strong {
    display: block;
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(1.05)};
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    display: block;
    color: ${muted};
    font-size: ${textSize(0.8)};
  }
`;

export const SheetBody = styled.div`
  ${touchFeedback}

  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 0.75rem 0.75rem 0;
`;
